import { useEffect, useState } from 'react';
import { useAuthProvider } from 'utils/api/useAuthProvider';
import { formatErrorMessage } from 'utils/util/error';
import { OrgChartRelatives } from '../../types';
import ConfirmAPI from '../../utils/api/ConfirmAPI';
import { getValidDirectReports } from '../../utils/models/Person';

const usePersonOrgChartRelatives = (
  currentOrganization,
  currentProxyPerson,
  person,
  enabled = true
) => {
  const [orgChartRelatives, setOrgChartRelatives] = useState<
    OrgChartRelatives | undefined
  >(undefined);
  const [isMounted, setIsMounted] = useState(false);
  const { user } = useAuthProvider();
  const userSub = user?.sub;

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    if (!enabled || !isMounted || !person) {
      return;
    }

    const params = {
      person: person.id,
      organization: currentOrganization?.id,
    };

    ConfirmAPI.getUrlWithCache(
      '/get-org-chart-relatives',
      'get-org-chart-relatives-self',
      userSub,
      currentProxyPerson,
      params,
      (data) => {
        if (isMounted) {
          if (data?.results) {
            setOrgChartRelatives({
              manager: data.results.manager ? data.results.manager : null,
              additional_managers: data.results.additional_managers || [],
              above_manager: data.results.above_manager,
              direct_reports: getValidDirectReports(
                data.results.direct_reports
              ),
              peers: data.results.peers,
            });
          }
        }
      },
      (message) => {
        // instead of hard-failing the entire page, log the error but display nothing
        // in the "view team member objectives" side area
        // NOTE: Do not print error message if the status is 404 since
        // it's a common case of impersonating an org without a person
        if (message?.status !== 404) {
          console.error(
            'Unable to fetch org chart relatives: ' +
              formatErrorMessage(message)
          );
        }

        setOrgChartRelatives({
          manager: null,
          additional_managers: [],
          above_manager: [],
          direct_reports: [],
          peers: [],
        });
      }
    );
  }, [
    enabled,
    isMounted,
    currentOrganization?.id,
    currentProxyPerson,
    person,
    userSub,
  ]);

  return orgChartRelatives;
};

export default usePersonOrgChartRelatives;
